import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { CircleHelp } from 'lucide-react'
import FormPage from '../../components/FormPage'
import PageLoader from '../../components/PageLoader'
import { UnavailableRecordState } from '../../components/States'
import useGoBack from '../../components/useGoBack'
import { useToast } from '../../components/toastContext'
import QuestionFields from './QuestionFields'
import { EMPTY_QUESTION, validateQuestion } from './questionForm'
import { getQuestion, getQuestionsPage, saveQuestion } from './contentService'
import { questionsStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'

/**
 * إضافة سؤال / تعديل سؤال — a question from the bank, on its own page.
 *
 * It was an extra-wide sheet over the list: two languages of the question, four options
 * in each, two images and three choices, in a box that scrolled inside a box. Here the
 * form has the column to itself and the browser's Back leaves it.
 *
 * `/questions/new?categoryId=` starts the question in that topic — the list passes its
 * own topic filter, so adding while looking at one topic files the question there.
 */
export default function QuestionFormPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const goBack = useGoBack('/questions')
  const toast = useToast()

  const [values, setValues] = useState(EMPTY_QUESTION)
  const [errors, setErrors] = useState({})
  const [stored, setStored] = useState(null)
  const [lookups, setLookups] = useState({ categories: [], difficulties: [] })
  const [lookupsLoading, setLookupsLoading] = useState(true)
  const [lookupsError, setLookupsError] = useState(null)
  const [loading, setLoading] = useState(!!id)
  const [loadedId, setLoadedId] = useState(id ? null : '')
  const [loadError, setLoadError] = useState(null)
  const [missing, setMissing] = useState(false)
  const [saving, setSaving] = useState(false)

  /* The topic and difficulty options, VALUED BY ID — `SaveQuestion` parses ints. */
  useEffect(() => {
    let alive = true
    getQuestionsPage()
      .then((page) => alive && setLookups({ categories: page.categories ?? [], difficulties: page.difficulties ?? [] }))
      .catch((error) => alive && setLookupsError(error ?? new Error('Lookup load failed')))
      .finally(() => alive && setLookupsLoading(false))
    return () => {
      alive = false
    }
  }, [])

  /* The list row is ONE language — `QuestionItemDto` is localized server-side — and the
     form edits both, so an edit always reads the question itself. */
  useEffect(() => {
    setErrors({})
    setLoadError(null)
    setMissing(false)

    if (!id) {
      setStored(null)
      setValues({ ...EMPTY_QUESTION, category: params.get('categoryId') ?? '' })
      setLoadedId('')
      setLoading(false)
      return undefined
    }

    let alive = true
    setLoading(true)
    getQuestion(id)
      .then((question) => {
        if (!alive) return
        if (!question) {
          setMissing(true)
        } else {
          setValues({
            ...EMPTY_QUESTION,
            ...question,
            answer: String(question.answer ?? ''),
            category: question.category ?? '',
            difficulty: question.difficulty ?? '',
            /* The stored images are URLs, drawn beside the picker; the fields start empty
               so a save without a new pick keeps them. */
            imageAr: null,
            imageEn: null,
          })
          setStored({ imageAr: question.imageAr, imageEn: question.imageEn })
        }
        setLoadedId(id)
        setLoading(false)
      })
      .catch((error) => {
        if (!alive) return
        setLoadError(error ?? new Error('load failed'))
        setLoadedId(id)
        setLoading(false)
      })

    return () => {
      alive = false
    }
    // The topic filter seeds a NEW question once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const validateStep = () => {
    const found = validateQuestion(values, { askTopic: true })
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      const outcome = await saveQuestion({ ...values, id: id ? Number(id) : undefined, answer: Number(values.answer) })
      if (!outcome.ok) {
        toast.error(serverDetail(outcome.message) || S.deleteFailed)
        return
      }
      toast.success(serverText(outcome.message) || S.saved)
      goBack()
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.deleteFailed)
    } finally {
      setSaving(false)
    }
  }

  if (loading || lookupsLoading || loadedId !== (id ?? '')) return <PageLoader />
  if (missing || loadError || lookupsError) return <UnavailableRecordState error={loadError || lookupsError} home="/questions" />

  return (
    <FormPage
      icon={CircleHelp}
      title={id ? S.form.editTitle : S.actions.create}
      subtitle={S.title}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      <QuestionFields
        values={values}
        errors={errors}
        set={set}
        categories={lookups.categories}
        levels={lookups.difficulties}
        stored={stored}
        askTopic
      />
    </FormPage>
  )
}
